import React, { ReactNode, Component, ErrorInfo, FunctionComponent } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { datadogLogs } from "@datadog/browser-logs";
import { colours } from "@/utils/themeUtils";

interface ErrorBoundaryProps {
	fallback?: ReactNode; // if you want to customise the fallback UI
	children: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
		};
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error("Error caught by ErrorBoundary:", error, errorInfo);

		// Log to Datadog with detailed error information
		datadogLogs.logger.error("React Error Boundary caught an error", {
			error: {
				name: error.name,
				message: error.message,
				stack: error.stack,
			},
			errorInfo: {
				componentStack: errorInfo.componentStack,
			},
			location: window.location.href,
		});
	}

	render(): ReactNode {
		const { hasError } = this.state;
		const { fallback } = this.props;

		if (hasError) {
			return fallback ? fallback : <DefaultFallback />;
		}

		// Render the children if no error occurred
		return this.props.children;
	}
}

const DefaultFallback: FunctionComponent = (): JSX.Element => {
	return (
		<Flex justifyContent={"space-between"}>
			<Box></Box>
			<Box
				display={"inline-block"}
				alignItems={"center"}
				borderRadius="lg"
				p={4}
				px={5}
				backgroundColor={colours.danger.background.weaker}
				my={5}
				border="1px solid"
				borderColor={colours.danger.background.medium}
				boxShadow="sm"
			>
				<Text textAlign={"center"} color={colours.danger.icon}>
					An unexpected error occurred. Please refresh the page, or contact support if the issue persists.
				</Text>
			</Box>
			<Box></Box>
		</Flex>
	);
};

export default ErrorBoundary;
